import {
  collection, doc, setDoc, getDocs, query, orderBy, limit, deleteDoc,
} from 'firebase/firestore';
import { db } from './firebaseService';
import { unwrapSyntheticSpotId } from './waypointService';
import type { VehicleCategory } from '../types';

export interface SpotVehicleVisit {
  userId: string;
  vehicleCategory: VehicleCategory;
  vehicleLengthCm?: number;
  photoUrl?: string;
  checkInAt: number;
  nights?: number;
  source: 'live' | 'retro';
}

export interface SpotVehicleVisitInput {
  userId: string;
  spotId: string;
  checkInAt: number;
  vehicleCategory?: VehicleCategory;
  vehicleLengthCm?: number;
  photoUrl?: string | null;
  nights?: number;
  source: 'live' | 'retro';
  /** Default true — bruker kan skru av i garasjen. */
  shareOnSpots?: boolean;
}

export function spotVehicleVisitDocId(userId: string, checkInAt: number): string {
  return `${userId.replace(/\//g, '_')}_${checkInAt}`;
}

/** Om vi skal skrive en offentlig kjøretøy-rad for plassen. */
export function shouldRecordSpotVehicleVisit(input: SpotVehicleVisitInput): boolean {
  if (input.shareOnSpots === false) return false;
  return !!input.vehicleCategory;
}

/**
 * Snapshot av aktivt garasje-kjøretøy ved innsjekk/retro-opphold.
 * Lagres under cached_spots/{spotId}/vehicle_visits for offentlig lesing.
 */
export async function writeSpotVehicleVisit(input: SpotVehicleVisitInput): Promise<void> {
  if (!shouldRecordSpotVehicleVisit(input) || !input.vehicleCategory) return;

  const spotId = unwrapSyntheticSpotId(input.spotId);
  const ref = doc(
    db,
    'cached_spots',
    spotId,
    'vehicle_visits',
    spotVehicleVisitDocId(input.userId, input.checkInAt),
  );

  const payload: SpotVehicleVisit = {
    userId: input.userId,
    vehicleCategory: input.vehicleCategory,
    checkInAt: input.checkInAt,
    source: input.source,
    ...(input.vehicleLengthCm ? { vehicleLengthCm: input.vehicleLengthCm } : {}),
    ...(input.photoUrl ? { photoUrl: input.photoUrl } : {}),
    ...(input.nights && input.nights > 0 ? { nights: input.nights } : {}),
  };

  await setDoc(ref, payload, { merge: true });
}

export async function deleteSpotVehicleVisit(
  userId: string,
  spotId: string,
  checkInAt: number,
): Promise<void> {
  const id = unwrapSyntheticSpotId(spotId);
  await deleteDoc(
    doc(db, 'cached_spots', id, 'vehicle_visits', spotVehicleVisitDocId(userId, checkInAt)),
  ).catch(() => {});
}

export async function fetchSpotVehicleVisits(
  spotId: string,
  maxResults = 24,
): Promise<SpotVehicleVisit[]> {
  const id = unwrapSyntheticSpotId(spotId);
  const q = query(
    collection(db, 'cached_spots', id, 'vehicle_visits'),
    orderBy('checkInAt', 'desc'),
    limit(maxResults),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data() as SpotVehicleVisit);
}

/** Netter mellom to tidspunkter (inkl. startdag), minimum 1. */
export function nightsBetween(checkInAt: number, checkedOutAt?: number): number {
  if (!checkedOutAt || checkedOutAt <= checkInAt) return 1;
  return Math.max(1, Math.floor((checkedOutAt - checkInAt) / 86_400_000) + 1);
}
